import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  SupplierClaimOfferCard,
  type SupplierClaimOfferCardProps,
} from '@/components/onboarding/SupplierClaimOfferCard';
import type { SupplierClaimDecisionGate } from '@/hooks/useSupplierClaimDecisionGate';

export interface SupplierClaimOfferSlotProps
  extends Omit<SupplierClaimOfferCardProps, 'candidate'> {
  gate: SupplierClaimDecisionGate;
  /** Extra conditions for showing a known offer (e.g. not dismissed, first step). */
  showOffer?: boolean;
}

/**
 * ENT-6459: the one place a customer-path screen shows the supplier claim offer, or why its
 * decision controls are waiting: a check in flight, or an unknown answer with Retry and
 * Continue as a customer. Unknown is shown as unknown, never as "no offer".
 */
export function SupplierClaimOfferSlot({ gate, showOffer = true, ...cardProps }: SupplierClaimOfferSlotProps) {
  if (gate.state === 'checking') {
    return (
      <div
        role="status"
        data-testid="supplier-claim-offer-checking"
        className="flex items-center gap-2 rounded-lg border border-border/70 bg-muted/30 px-4 py-3 text-sm text-muted-foreground"
      >
        <Loader2 className="h-4 w-4 animate-spin" />
        Checking for a supplier profile…
      </div>
    );
  }
  if (gate.state === 'unknown') {
    return (
      <div
        role="status"
        data-testid="supplier-claim-offer-unknown"
        className="space-y-3 rounded-lg border border-border/70 bg-muted/30 px-4 py-3 text-sm"
      >
        <p>We couldn't check whether this email belongs to a listed supplier.</p>
        <div className="flex flex-wrap gap-2">
          <Button type="button" size="sm" onClick={gate.retry} data-testid="supplier-claim-offer-retry">
            Retry
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={gate.continueAsCustomer}
            data-testid="supplier-claim-offer-continue-as-customer"
          >
            Continue as a customer
          </Button>
        </div>
      </div>
    );
  }
  if (!showOffer || !gate.candidate) return null;
  return <SupplierClaimOfferCard candidate={gate.candidate} {...cardProps} />;
}
